<template>
  <v-container class="modern-detail-view pb-12 bg-light-grey min-vh-100" fluid>
    <v-fade-transition>
      <div v-if="loading" class="refresh-overlay">
        <v-card class="pa-6 rounded-xl shadow-lg text-center">
          <v-progress-circular indeterminate color="primary" size="40" class="mb-4" />
          <div class="text-subtitle-2 font-weight-black primary--text uppercase tracking-widest">Cargando Detalles</div>
        </v-card>
      </div>
    </v-fade-transition>

    <div v-if="salesNote && salesNote.id" class="px-4 pt-4">
      <!-- Premium Global Header (Master Pattern) -->
      <v-row class="mb-8 align-center">
        <v-col cols="12" md="8">
          <div class="d-flex align-center flex-wrap gap-3 mb-2">
            <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white" small>fas fa-file-invoice-dollar</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center gap-3">
                <h1 class="text-h3 font-weight-black primary--text lh-1">
                  Cotización #{{ salesNote.id }}
                </h1>
                <v-chip
                  v-if="salesNote.work_order"
                  color="success lighten-4"
                  text-color="success darken-2"
                  label
                  class="font-weight-black text-uppercase shadow-sm px-4"
                >
                  <v-icon x-small left color="success darken-2">fas fa-check-circle</v-icon>
                  Aprobada (OT #{{ salesNote.work_order }})
                </v-chip>
                <v-chip
                  v-else
                  color="info lighten-4"
                  text-color="info darken-2"
                  label
                  class="font-weight-black text-uppercase shadow-sm px-4"
                >
                  <v-icon x-small left color="info darken-2">fas fa-calculator</v-icon>
                  En Proceso / Costeada
                </v-chip>
              </div>
              <div class="d-flex align-center mt-2 gap-4 flex-wrap">
                <div class="caption grey--text font-weight-bold d-flex align-center">
                  <v-icon x-small color="grey lighten-1" class="mr-1">fas fa-user-plus</v-icon>
                  CREADA POR: {{ salesNote.created_by_full_name }} — {{ salesNote.created_at | moment('DD/MM/YYYY HH:mm') }}
                </div>
                <div v-if="salesNote.updated_by" class="caption grey--text font-weight-bold d-flex align-center">
                  <v-icon x-small color="grey lighten-1" class="mr-1">fas fa-pen-nib</v-icon>
                  ACTUALIZADA: {{ salesNote.updated_by_full_name }} — {{ salesNote.updated_at | moment('DD/MM/YYYY HH:mm') }}
                </div>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="text-right">
          <div class="d-flex justify-end align-center gap-2">
            <v-btn
              color="primary"
              class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-sm"
              :disabled="Boolean(salesNote.work_order)"
              @click="edit"
            >
              <v-icon left size="14">fas fa-pen</v-icon>
              Modificar
            </v-btn>

            <v-menu offset-y left transition="slide-y-transition" rounded="lg">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" v-bind="attrs" v-on="on" class="grey lighten-4 shadow-sm h-44 w-44">
                  <v-icon small>fas fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1 bespoke-menu">
                <v-list-item @click="generateQuotation" class="px-4">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-print</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Presupuesto Cliente</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="$can('readCost', 'SalesNote')" @click="generateCostReport" class="px-4">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-calculator</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Informe de Costos</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="$can('duplicate', 'SalesNote')" @click="duplicate" class="px-4">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-clone</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Duplicar Cotización</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn icon @click="$router.go(-1)" class="grey lighten-4 shadow-sm h-44 w-44">
              <v-icon small>fas fa-arrow-left</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Bespoke Enterprise KPI Cards (Root Level Performance) -->
      <v-row dense class="mb-8">
        <v-col cols="12" sm="2">
          <v-card class="bespoke-mini-card flat-border text-center pa-4" flat outlined>
            <div class="metric-label">Mts² Totales</div>
            <div class="text-h6 font-weight-black primary--text tracking-tighter">{{ salesNote.totals.mts2 | number('0,0.00') }}</div>
            <div class="caption grey--text uppercase font-weight-bold mt-1">Superficie</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="2" v-if="$can('readCost', 'SalesNote')">
          <v-card class="bespoke-mini-card flat-border text-center pa-4" flat outlined>
            <div class="metric-label">Costo Total</div>
            <div class="text-h6 font-weight-black purple--text tracking-tighter">{{ salesNote.totals.cost | moneda }}</div>
            <div class="caption grey--text uppercase font-weight-bold mt-1">Inversión</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="2">
          <v-card class="bespoke-mini-card flat-border text-center pa-4" flat outlined>
            <div class="metric-label">Valor Venta</div>
            <div class="text-h6 font-weight-black tracking-tighter" :class="salesNote.totals.price > salesNote.totals.cost ? 'primary--text' : 'error--text'">
              {{ salesNote.totals.price | moneda }}
            </div>
            <div class="caption grey--text uppercase font-weight-bold mt-1">Precio Base</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="2">
          <v-card class="bespoke-mini-card flat-border text-center pa-4" flat outlined>
            <div class="metric-label">Descuento</div>
            <div class="text-h6 font-weight-black orange--text tracking-tighter">
              {{ salesNote.totals.discount }}%
            </div>
            <div class="caption grey--text uppercase font-weight-bold mt-1">(-{{ salesNote.totals.totalDiscount | moneda }})</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="2">
          <v-card class="bespoke-mini-card flat-border text-center pa-4 primary lighten-5 border-primary-light" flat outlined>
            <div class="metric-label primary--text">Total Neto</div>
            <div class="text-h5 font-weight-black primary--text tracking-tighter">{{ salesNote.totals.total | moneda }}</div>
            <div class="caption primary--text opacity-70 uppercase font-weight-bold mt-1">Venta Final</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="2" v-if="$can('readCost', 'SalesNote')">
          <v-card class="bespoke-mini-card flat-border text-center pa-4" :class="salesNote.totals.margin > 0.3 ? 'success lighten-5' : 'warning lighten-5'" flat outlined>
            <div class="metric-label" :class="salesNote.totals.margin > 0.3 ? 'success--text' : 'warning--text'">Margen</div>
            <div class="text-h5 font-weight-black tracking-tighter" :class="salesNote.totals.margin > 0.3 ? 'success--text' : 'warning--text text--darken-2'">
              {{ salesNote.totals.margin | percent(1) }}
            </div>
            <div class="caption uppercase font-weight-bold mt-1" :class="salesNote.totals.margin > 0.3 ? 'success--text' : 'warning--text'">Utilidad Estimada</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Detailed Information Grid -->
      <v-row class="mb-8">
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden shadow-xs">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-building</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase tracking-wider">Cliente y Proyecto</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div class="mb-4">
                <div class="caption grey--text mb-1 uppercase font-weight-bold tracking-widest">Razón Social</div>
                <div class="text-h6 font-weight-black primary--text lh-1-2">{{ salesNote.sales_note_customer.name }}</div>
                <div class="caption grey--text mt-1">RUT: {{ salesNote.sales_note_customer.rut }}</div>
              </div>
              <div>
                <div class="caption grey--text mb-1 uppercase font-weight-bold tracking-widest">Nombre del Proyecto</div>
                <div class="body-1 font-weight-medium grey--text text--darken-4">{{ salesNote.name }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden shadow-xs">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-user-tie</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase tracking-wider">Contacto y Responsable</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div class="mb-4">
                <div class="caption grey--text mb-1 uppercase font-weight-bold tracking-widest">Contacto Directo</div>
                <div class="body-1 font-weight-black grey--text text--darken-4 d-flex align-center">
                  <v-avatar size="24" color="blue lighten-5" class="mr-2">
                    <span class="caption font-weight-black blue--text">{{ salesNote.sales_note_contact.first_name[0] }}</span>
                  </v-avatar>
                  {{ salesNote.sales_note_contact.first_name }} {{ salesNote.sales_note_contact.last_name }}
                </div>
              </div>
              <div v-if="salesNote.sales_note_customer.seller">
                <div class="caption grey--text mb-1 uppercase font-weight-bold tracking-widest">Responsable Comercial</div>
                <div class="body-1 font-weight-medium grey--text text--darken-4 d-flex align-center">
                  <v-avatar size="24" color="primary lighten-4" class="mr-2">
                    <span class="caption font-weight-black primary--text">{{ salesNote.sales_note_customer.seller.first_name[0] }}</span>
                  </v-avatar>
                  {{ salesNote.sales_note_customer.seller.first_name }} {{ salesNote.sales_note_customer.seller.last_name }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden shadow-xs">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-info-circle</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase tracking-wider">Observaciones Generales</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div v-if="salesNote.details.notes" class="body-2 font-italic grey--text text--darken-3">
                "{{ salesNote.details.notes }}"
              </div>
              <div v-else class="caption font-italic grey--text py-4 text-center">
                Sin notas ni observaciones adicionales registradas.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content Tabs -->
      <v-card outlined class="modern-card elevation-0 overflow-hidden border-rounded shadow-sm">
        <v-tabs v-model="tab" background-color="grey lighten-5" color="primary" slider-size="4" grow class="border-bottom">
          <v-tab class="text-none font-weight-black py-5">
            <v-icon left size="16">fas fa-boxes</v-icon> Listado de Productos
          </v-tab>
          <v-tab v-if="salesNote.details.costs && salesNote.details.costs.length > 0" class="text-none font-weight-black py-5">
            <v-icon left size="16">fas fa-plus-circle</v-icon> Costos Adicionales
          </v-tab>
          <v-tab class="text-none font-weight-black py-5">
            <v-icon left size="16">fas fa-paperclip</v-icon> Documentos y Distribución
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Productos Tab -->
          <v-tab-item class="bg-white">
            <v-data-table
              :headers="productsTableHeaders"
              :items="salesNote.details.products"
              :single-expand="true"
              :expanded.sync="expanded"
              item-key="key"
              show-expand
              class="modern-table elevation-0"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:item.name="{ item }">
                <div class="py-4">
                  <div class="body-2 font-weight-black primary--text">{{ item.name }}</div>
                  <div v-if="item.sku" class="caption grey--text font-weight-bold mt-1">SKU: {{ item.sku }}</div>
                </div>
              </template>

              <template v-slot:item.qty="{ item }">
                <span class="body-2 font-weight-black">{{ item.qty }}</span>
                <span class="caption grey--text ml-1">un.</span>
              </template>

              <template v-slot:item.totalMts2="{ item }">
                <v-chip x-small outlined label color="grey darken-1" class="font-weight-black">
                  {{ item.totalMts2 | number('0,0.00') }} m²
                </v-chip>
              </template>

              <template v-slot:item.unitCost="{ item }">
                <div class="d-flex flex-column align-end" v-if="$can('readCost', 'SalesNote')">
                  <span class="caption font-weight-bold purple--text">{{ item.unitCost | moneda }}</span>
                </div>
              </template>

              <template v-slot:item.totalCost="{ item }">
                <div class="d-flex flex-column align-end" v-if="$can('readCost', 'SalesNote')">
                  <span class="body-2 font-weight-black purple--text">{{ item.totalCost | moneda }}</span>
                </div>
              </template>

              <template v-slot:item.price="{ item }">
                <div class="d-flex align-center justify-end">
                  <span class="caption font-weight-bold grey--text text--darken-3">{{ item.price | moneda }}</span>
                  <v-icon x-small class="ml-2 color-grey-light">{{ item.sell_unit_m2 ? 'fas fa-vector-square' : 'fas fa-box' }}</v-icon>
                </div>
              </template>

              <template v-slot:item.totalPrice="{ item }">
                <div class="d-flex flex-column align-end">
                  <span class="body-2 font-weight-black" :class="item.totalPrice > item.totalCost ? 'success--text' : 'error--text'">
                    {{ item.totalPrice | moneda }}
                  </span>
                  <v-chip v-if="item.totalPrice < item.totalCost" x-small color="error" label class="mt-1 font-weight-black py-0 px-1" style="height: 16px">
                    BAJO COSTO
                  </v-chip>
                </div>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <div class="pa-8 bg-grey-light border-left-primary">
                    <v-row>
                      <v-col cols="12" md="6">
                        <recipe
                          class="modern-card shadow-sm h-100"
                          :mts2="item.totalMts2"
                          :total="item.totalCost"
                          :qty="item.qty"
                          :items="item.product_receta"
                          :readonly="true"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <supplies
                          class="modern-card shadow-sm h-100"
                          :mts2="item.totalMts2"
                          :total="item.totalCost"
                          :qty="item.qty"
                          :items="item.product_supplies"
                          :readonly="true"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="item.notes" class="mt-4 mx-0">
                      <v-col cols="12" class="pa-4 bg-white rounded-lg border">
                        <div class="caption grey--text uppercase font-weight-bold mb-2 tracking-widest">Notas Técnicas del Item</div>
                        <div class="body-2 font-italic grey--text text--darken-3">"{{ item.notes }}"</div>
                      </v-col>
                    </v-row>
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Costos Adicionales Tab -->
          <v-tab-item v-if="salesNote.details.costs && salesNote.details.costs.length > 0" class="bg-white pa-6">
            <v-data-table
              :headers="proyectCostsHeaders"
              :items="salesNote.details.costs"
              class="modern-table elevation-0 border-rounded overflow-hidden"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" color="orange lighten-5" class="mr-3">
                    <v-icon x-small color="orange darken-2">fas fa-plus-circle</v-icon>
                  </v-avatar>
                  <span class="body-2 font-weight-black grey--text text--darken-4 font-body-2">{{ item.name }}</span>
                </div>
              </template>

              <template v-slot:item.unit="{ item }">
                <v-chip x-small label outlined class="grey--text text--darken-2 font-weight-bold">
                  {{ item.cost_unit.name }}
                </v-chip>
              </template>

              <template v-slot:item.unitCost="{ item }">
                <span class="caption font-weight-bold purple--text">{{ item.unitCost | moneda }}</span>
              </template>

              <template v-slot:item.totalCost="{ item }">
                <span class="body-2 font-weight-black purple--text">{{ item.totalCost | moneda }}</span>
              </template>

              <template v-slot:item.price="{ item }">
                <div class="d-flex align-center justify-end">
                  <span class="caption font-weight-bold grey--text text--darken-3">{{ item.price | moneda }}</span>
                  <v-icon v-if="item.unit && item.unit.id == 1" x-small class="ml-2 color-grey-light">fas fa-vector-square</v-icon>
                </div>
              </template>

              <template v-slot:item.totalPrice="{ item }">
                <div class="d-flex flex-column align-end">
                  <span class="body-2 font-weight-black" :class="item.totalPrice > item.totalCost ? 'success--text' : 'error--text'">
                    {{ item.totalPrice | moneda }}
                  </span>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Documentos y Distribución Tab -->
          <v-tab-item class="bg-white pa-8">
            <v-row>
              <v-col cols="12" md="6">
                <v-sheet color="grey lighten-5" class="pa-6 rounded-xl border">
                  <div class="d-flex align-center mb-6">
                    <v-icon color="primary" class="mr-3">fas fa-share-alt</v-icon>
                    <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">Distribución del Proyecto</div>
                  </div>
                  <distribution-list :sales_note="salesNote" />
                </v-sheet>
              </v-col>
              <v-col cols="12" md="6">
                <v-sheet color="grey lighten-5" class="pa-6 rounded-xl border h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-6">
                    <v-icon color="primary" class="mr-3">fas fa-file-alt</v-icon>
                    <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">Resumen de Archivos</div>
                  </div>
                  <div class="flex-grow-1 d-flex flex-column align-center justify-center py-12" v-if="!salesNote.files || salesNote.files.length === 0">
                    <v-icon x-large class="mb-4 opacity-20">fas fa-folder-open</v-icon>
                    <div class="caption grey--text font-weight-bold uppercase tracking-widest text-center">No hay documentos cargados específicamente en esta vista</div>
                  </div>
                  <div v-else class="d-flex flex-wrap gap-2">
                     <v-chip v-for="file in salesNote.files" :key="file.id" label color="white" class="border shadow-xs" link :href="file.url" target="_blank">
                       <v-icon left x-small color="primary">fas fa-file-alt</v-icon>
                       {{ file.name }}
                     </v-chip>
                  </div>
                </v-sheet>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import Recipe from '@/components/products/products/Recipe'
import DeliveryEstimation from '@/components/sales/DeliveryEstimation.vue'
import Supplies from '@/components/products/products/Supplies'
import DistributionList from '@/components/sales/DistributionList'
import { pdfQuotation } from '@/doc_generators/quotation'
import { pdfCostReport } from '@/doc_generators/costReport'

export default {
  name: 'SalesNoteDetail',
  components: { Recipe, Supplies, DeliveryEstimation, DistributionList },
  props: {
    // Para cuando se usa como componente o recibe via params
    sales_note_prop: { type: Object, default: null }
  },
  data() {
    return {
      salesNote: null,
      loading: false,
      tab: null,
      expanded: [],
      productsTableHeaders: [
        { text: 'Producto / Descripción', value: 'name', class: 'table-header-custom' },
        { text: 'Cant.', value: 'qty', align: 'center', class: 'table-header-custom', width: '100' },
        { text: 'Ancho', value: 'width', align: 'end', class: 'table-header-custom', width: '80' },
        { text: 'Alto', value: 'height', align: 'end', class: 'table-header-custom', width: '80' },
        { text: 'Superficie', value: 'totalMts2', align: 'end', class: 'table-header-custom', width: '100' },
        { text: 'Costo Unit.', value: 'unitCost', align: 'end', class: 'table-header-custom' },
        { text: 'Costo Total', value: 'totalCost', align: 'end', class: 'table-header-custom' },
        { text: 'Precio Unit.', value: 'price', align: 'end', class: 'table-header-custom' },
        { text: 'Precio Total', value: 'totalPrice', align: 'end', class: 'table-header-custom' },
        { text: '', value: 'data-table-expand', class: 'table-header-custom', width: '50' },
      ],
      proyectCostsHeaders: [
        { text: 'Gasto Adicional', value: 'name', class: 'table-header-custom' },
        { text: 'Cantidad', value: 'qty', align: 'center', class: 'table-header-custom', width: '100' },
        { text: 'Unidad', value: 'unit', align: 'center', class: 'table-header-custom' },
        { text: 'Costo Unit.', value: 'unitCost', align: 'end', class: 'table-header-custom' },
        { text: 'Costo Total', value: 'totalCost', align: 'end', class: 'table-header-custom' },
        { text: 'Precio Unit.', value: 'price', align: 'end', class: 'table-header-custom' },
        { text: 'Precio Total', value: 'totalPrice', align: 'end', class: 'table-header-custom' },
      ]
    }
  },
  created() {
    if (this.$route.params.sales_note) {
      this.salesNote = this.$route.params.sales_note
    } else if (this.sales_note_prop) {
      this.salesNote = this.sales_note_prop
    }
    // Si no viene por prop ni params, lo cargaremos si fuera necesario (aquí falta el fetch por ID)
  },
  methods: {
    edit() {
      this.$router.push({ name: 'SalesNoteCreateUpdate', params: { id: this.salesNote.id } })
    },
    duplicate() {
      this.$router.push({ name: 'SalesNoteCreateUpdate', params: { id: this.salesNote.id, duplicate: true } })
    },
    generateQuotation() {
      pdfQuotation(this.salesNote)
    },
    generateCostReport() {
      pdfCostReport(this.salesNote)
    },
  }
}
</script>

<style scoped>
.modern-detail-view { background-color: #f0f2f5; font-family: 'Outfit', sans-serif !important; }
.bg-light-grey { background-color: #f8fafc; }
.min-vh-100 { min-height: 100vh; }

.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; background: white; }
.border-rounded { border-radius: 12px !important; }
.shadow-xs { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important; }
.shadow-xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; }

/* Custom Headers and Tabs */
.table-header-custom { background-color: #f8fbfc !important; text-transform: uppercase; font-size: 0.65rem !important; font-weight: 800 !important; color: #64748b !important; letter-spacing: 1px !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid rgba(0,0,0,0.03) !important; }
.modern-table >>> .v-data-table-header { border-bottom: 2px solid #e2e8f0 !important; }

/* KPI Mini Cards */
.bespoke-mini-card { position: relative; background: white !important; border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; transition: all 0.3s ease; }
.metric-label { font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
.tracking-tighter { letter-spacing: -1px; }
.tracking-wider { letter-spacing: 0.5px; }

/* Layout Utilities */
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }
.opacity-20 { opacity: 0.2; }
.opacity-70 { opacity: 0.7; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.5px; }
.h-44 { height: 44px !important; }
.w-44 { width: 44px !important; min-width: 44px !important; }

/* Visual Elements */
.bg-grey-light { background-color: #f9fafb; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.border-primary-light { border-color: rgba(var(--v-primary-base), 0.2) !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06); }
.color-grey-light { color: #cbd5e1; }

.refresh-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); display: flex; align-items: center; justify-content: center; z-index: 100; }

.bespoke-menu { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.1) !important; }
</style>
